/**
 * Dashboard studio — le rapport filtrable, servi à la racine pour un
 * utilisateur dont le module actif est « studio ».
 *
 * Tout vient de `/studio/rapport/`, qui applique lui-même les permissions :
 * `peut_voir_finances` décide si commissions, dépenses et bénéfice sont
 * renvoyés. On n'affiche donc jamais un chiffre que le serveur a masqué.
 *
 * Filtrer sur un client omet volontairement dépenses et bénéfice côté serveur
 * (`filtre_client`) : les dépenses du salon ne sont pas imputables à une
 * cliente.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Card, CardContent, Typography, TextField, MenuItem, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip,
  CircularProgress, Alert, Divider, Collapse, IconButton, Autocomplete, Tabs, Tab,
} from '@mui/material';
import {
  TrendingUp, Payments, Receipt, People, Refresh,
  KeyboardArrowDown, KeyboardArrowRight, ContentCut, LocalCafe,
} from '@mui/icons-material';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip,
} from 'recharts';
import { toast } from 'react-toastify';

import {
  rapportStudioAPI, clientStudioAPI, extraireListe,
} from '../../services/studioAPI';

const htg = (valeur) => `${Number(valeur || 0).toLocaleString()} HTG`;

const isoLocal = (date) => {
  const mois = String(date.getMonth() + 1).padStart(2, '0');
  const jour = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${mois}-${jour}`;
};

const premierDuMois = () => {
  const date = new Date();
  return new Date(date.getFullYear(), date.getMonth(), 1);
};

const ilYA = (jours) => {
  const date = new Date();
  date.setDate(date.getDate() - jours);
  return date;
};

const PERIODES = [
  { code: 'jour', label: "Aujourd'hui", debut: () => new Date() },
  { code: 'semaine', label: '7 derniers jours', debut: () => ilYA(6) },
  { code: 'mois', label: 'Ce mois', debut: premierDuMois },
];

const dateHeure = (iso) => {
  if (!iso) return '—';
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleString('fr-FR', {
        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
      });
};

const Kpi = ({ icone, couleur, valeur, label }) => (
  <Card sx={{ flex: '1 1 200px' }}>
    <CardContent>
      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
        {React.cloneElement(icone, { sx: { color: couleur } })}
        <Typography variant="body2" color="text.secondary">{label}</Typography>
      </Box>
      <Typography variant="h4" fontWeight="bold">{valeur}</Typography>
    </CardContent>
  </Card>
);

const LigneTicket = ({ ticket, finances }) => {
  const [ouvert, setOuvert] = useState(false);

  return (
    <>
      <TableRow hover sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell width={48}>
          <IconButton size="small" onClick={() => setOuvert(!ouvert)}>
            {ouvert ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
          </IconButton>
        </TableCell>
        <TableCell>{ticket.numero}</TableCell>
        <TableCell>{dateHeure(ticket.date_paiement)}</TableCell>
        <TableCell>
          {ticket.client_nom
            ? <Typography fontWeight={600}>{ticket.client_nom}</Typography>
            : <Typography color="text.disabled" fontStyle="italic">Sans fiche client</Typography>}
        </TableCell>
        <TableCell>
          <Chip size="small" label={ticket.methode_paiement || '—'} variant="outlined" />
        </TableCell>
        <TableCell align="right">
          <Typography fontWeight={600}>{htg(ticket.montant_total)}</Typography>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={6} sx={{ py: 0, borderBottom: ouvert ? undefined : 'none' }}>
          <Collapse in={ouvert} timeout="auto" unmountOnExit>
            <Box my={2} ml={4}>
              <Typography variant="subtitle2" gutterBottom>Qui a fait quoi</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Service</TableCell>
                    <TableCell>Réalisé par</TableCell>
                    <TableCell align="center">Qté</TableCell>
                    <TableCell align="right">Montant</TableCell>
                    {finances && <TableCell align="right">Part</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ticket.lignes?.map((ligne) => (
                    <TableRow key={ligne.id}>
                      <TableCell>
                        {ligne.service}
                        {ligne.type_item === 'design' && (
                          <Chip size="small" label="design" sx={{ ml: 1 }} variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell>
                        {ligne.prestataire || (
                          <Typography variant="body2" color="text.disabled">
                            — (revente)
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">{ligne.quantite}</TableCell>
                      <TableCell align="right">{htg(ligne.total)}</TableCell>
                      {finances && (
                        <TableCell align="right">
                          <Typography variant="body2" color="success.main">
                            {ligne.pourcentage} % · {htg(ligne.commission)}
                          </Typography>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {Number(ticket.remise) > 0 && (
                <Typography variant="caption" color="error.main">
                  Remise appliquée : -{htg(ticket.remise)}
                </Typography>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const StudioDashboard = () => {
  const [rapport, setRapport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [onglet, setOnglet] = useState(0);

  const [periode, setPeriode] = useState('mois');
  const [dateDebut, setDateDebut] = useState(isoLocal(premierDuMois()));
  const [dateFin, setDateFin] = useState(isoLocal(new Date()));
  const [client, setClient] = useState(null);
  const [clients, setClients] = useState([]);

  const charger = useCallback(async (filtres) => {
    try {
      setLoading(true);
      setErreur(null);
      const reponse = await rapportStudioAPI.getRapport(filtres);
      setRapport(reponse.data);
    } catch (error) {
      const message = error.response?.data?.detail || error.message
        || 'Erreur lors du chargement du rapport';
      setErreur(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  // La liste sert à l'autocomplétion du filtre client
  useEffect(() => {
    clientStudioAPI.getAll()
      .then((reponse) => setClients(extraireListe(reponse)))
      .catch(() => setClients([]));
  }, []);

  useEffect(() => {
    charger({ date_debut: dateDebut, date_fin: dateFin, client: client?.id });
  }, [charger, dateDebut, dateFin, client]);

  const choisirPeriode = (code) => {
    const config = PERIODES.find((p) => p.code === code);
    if (!config) return;
    setPeriode(code);
    setDateDebut(isoLocal(config.debut()));
    setDateFin(isoLocal(new Date()));
  };

  const finances = rapport?.peut_voir_finances;
  const filtreClient = rapport?.filtre_client;

  const donneesPrestataires = useMemo(
    () => (rapport?.par_prestataire || []).map((p) => ({
      nom: p.nom,
      chiffre_affaires: p.chiffre_affaires,
      commission: p.commission || 0,
    })),
    [rapport]
  );

  if (loading && !rapport) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  const onglets = ['Vue d\'ensemble', 'Tickets'];
  const ongletActif = Math.min(onglet, onglets.length - 1);

  return (
    <Box m="1.5rem 2.5rem">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h4" fontWeight="bold">Studio</Typography>
          <Typography variant="body2" color="text.secondary">
            Du {dateDebut} au {dateFin}
            {client ? ` — ${client.nom}` : ''}
          </Typography>
        </Box>
        <Button
          startIcon={<Refresh />}
          onClick={() => charger({ date_debut: dateDebut, date_fin: dateFin, client: client?.id })}
        >
          Actualiser
        </Button>
      </Box>

      {erreur && <Alert severity="error" sx={{ mb: 2 }}>{erreur}</Alert>}

      {/* Filtres */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <TextField
            size="small" select label="Période" value={periode}
            onChange={(e) => choisirPeriode(e.target.value)}
            sx={{ minWidth: 190 }}
          >
            {PERIODES.map((p) => (
              <MenuItem key={p.code} value={p.code}>{p.label}</MenuItem>
            ))}
            <MenuItem value="perso">Période personnalisée</MenuItem>
          </TextField>
          <TextField
            size="small" type="date" label="Du" value={dateDebut}
            onChange={(e) => { setPeriode('perso'); setDateDebut(e.target.value); }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small" type="date" label="Au" value={dateFin}
            onChange={(e) => { setPeriode('perso'); setDateFin(e.target.value); }}
            InputLabelProps={{ shrink: true }}
          />
          <Autocomplete
            size="small"
            options={clients}
            value={client}
            onChange={(evenement, valeur) => setClient(valeur)}
            getOptionLabel={(option) =>
              option.telephone ? `${option.nom} (${option.telephone})` : option.nom
            }
            isOptionEqualToValue={(option, valeur) => option.id === valeur.id}
            renderInput={(params) => (
              <TextField {...params} label="Client" placeholder="Tous les clients" />
            )}
            sx={{ minWidth: 280, flex: '1 1 280px' }}
          />
        </Box>
      </Paper>

      {/* KPI */}
      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        <Kpi
          icone={<Payments />} couleur="success.main"
          valeur={htg(rapport?.chiffre_affaires)} label="Encaissé"
        />
        <Kpi
          icone={<Receipt />} couleur="info.main"
          valeur={rapport?.nombre_tickets ?? 0} label="Tickets"
        />
        <Kpi
          icone={<TrendingUp />} couleur="warning.main"
          valeur={htg(rapport?.panier_moyen)} label="Panier moyen"
        />
        {finances && !filtreClient && (
          <Kpi
            icone={<TrendingUp />}
            couleur={Number(rapport?.benefice) >= 0 ? 'success.main' : 'error.main'}
            valeur={htg(rapport?.benefice)}
            label={`Bénéfice (marge ${rapport?.marge_pourcent ?? 0} %)`}
          />
        )}
      </Box>

      <Tabs value={ongletActif} onChange={(e, valeur) => setOnglet(valeur)} sx={{ mb: 2 }}>
        {onglets.map((titre) => <Tab key={titre} label={titre} />)}
      </Tabs>

      {ongletActif === 0 ? (
        <>
          {/* Rentabilité */}
          {finances && (
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" gutterBottom>Rentabilité</Typography>
              <Box display="flex" justifyContent="space-between" py={0.5}>
                <Typography color="text.secondary">Chiffre d'affaires</Typography>
                <Typography>{htg(rapport?.chiffre_affaires)}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" py={0.5}>
                <Typography color="text.secondary">Part des prestataires</Typography>
                <Typography color="error.main">- {htg(rapport?.total_commissions)}</Typography>
              </Box>
              {Number(rapport?.articles?.cout_achat) > 0 && (
                <Box display="flex" justifyContent="space-between" py={0.5}>
                  <Typography color="text.secondary">
                    Achat des articles vendus
                  </Typography>
                  <Typography color="error.main">
                    - {htg(rapport.articles.cout_achat)}
                  </Typography>
                </Box>
              )}
              {filtreClient ? (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Filtré sur un client : les dépenses du salon ne lui étant pas imputables,
                  le bénéfice n'est pas calculé ici.
                </Alert>
              ) : (
                <>
                  <Box display="flex" justifyContent="space-between" py={0.5}>
                    <Typography color="text.secondary">Dépenses</Typography>
                    <Typography color="error.main">- {htg(rapport?.total_depenses)}</Typography>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box display="flex" justifyContent="space-between" py={0.5}>
                    <Typography fontWeight="bold">Bénéfice</Typography>
                    <Typography
                      fontWeight="bold"
                      color={Number(rapport?.benefice) >= 0 ? 'success.main' : 'error.main'}
                    >
                      {htg(rapport?.benefice)}
                    </Typography>
                  </Box>
                </>
              )}
            </Paper>
          )}

          {/* Prestataires */}
          {donneesPrestataires.length > 0 && (
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                {finances ? 'À payer aux prestataires' : 'Activité par prestataire'}
              </Typography>
              <Box height={280}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={donneesPrestataires}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="nom" />
                    <YAxis />
                    <RechartsTooltip formatter={(valeur) => htg(valeur)} />
                    <Bar dataKey="chiffre_affaires" name="Généré" fill="#3B82F6" />
                    {finances && <Bar dataKey="commission" name="Sa part" fill="#10B981" />}
                  </BarChart>
                </ResponsiveContainer>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Prestataire</TableCell>
                      <TableCell align="center">Prestations</TableCell>
                      <TableCell align="right">Généré</TableCell>
                      {finances && <TableCell align="right">Sa part</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rapport.par_prestataire.map((prestataire) => (
                      <TableRow key={prestataire.id}>
                        <TableCell>{prestataire.nom}</TableCell>
                        <TableCell align="center">{prestataire.nombre_prestations}</TableCell>
                        <TableCell align="right">{htg(prestataire.chiffre_affaires)}</TableCell>
                        {finances && (
                          <TableCell align="right">
                            <Typography fontWeight={600} color="success.main">
                              {htg(prestataire.commission)}
                            </Typography>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Répartitions */}
          <Box display="flex" gap={3} flexWrap="wrap" mb={3}>
            <Paper sx={{ p: 2, flex: '1 1 340px' }}>
              <Typography variant="h6" gutterBottom>Par section</Typography>
              {rapport?.par_categorie?.length ? rapport.par_categorie.map((categorie) => (
                <Box key={categorie.nom} display="flex" justifyContent="space-between" py={0.5}>
                  <Typography variant="body2">
                    {categorie.nom} <Typography component="span" color="text.disabled">×{categorie.nombre}</Typography>
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>{htg(categorie.total)}</Typography>
                </Box>
              )) : <Typography color="text.disabled">Aucune vente</Typography>}
            </Paper>

            <Paper sx={{ p: 2, flex: '1 1 340px' }}>
              <Typography variant="h6" gutterBottom>Ce qui marche le mieux</Typography>
              {rapport?.par_prestation?.length ? rapport.par_prestation.slice(0, 8).map((prestation) => (
                <Box key={prestation.id} display="flex" justifyContent="space-between" py={0.5}>
                  <Typography variant="body2">
                    {prestation.nom} <Typography component="span" color="text.disabled">×{prestation.nombre}</Typography>
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>{htg(prestation.total)}</Typography>
                </Box>
              )) : <Typography color="text.disabled">Aucune vente</Typography>}
            </Paper>

            <Paper sx={{ p: 2, flex: '1 1 340px' }}>
              <Typography variant="h6" gutterBottom>
                <People fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                Par client
              </Typography>
              {rapport?.par_client?.length ? rapport.par_client.slice(0, 8).map((c) => (
                <Box key={String(c.id)} display="flex" justifyContent="space-between" py={0.5}>
                  <Typography variant="body2">
                    {c.nom} <Typography component="span" color="text.disabled">×{c.nombre_tickets}</Typography>
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>{htg(c.total)}</Typography>
                </Box>
              )) : <Typography color="text.disabled">Aucune vente</Typography>}
            </Paper>

            {rapport?.par_article?.length > 0 && (
              <Paper sx={{ p: 2, flex: '1 1 340px' }}>
                <Typography variant="h6" gutterBottom>
                  <LocalCafe fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                  Articles vendus
                </Typography>
                {rapport.par_article.map((article) => (
                  <Box key={article.id} display="flex" justifyContent="space-between" py={0.5}>
                    <Typography variant="body2">
                      {article.nom} <Typography component="span" color="text.disabled">×{article.nombre}</Typography>
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>{htg(article.total)}</Typography>
                  </Box>
                ))}
                {finances && rapport?.articles && (
                  <>
                    <Divider sx={{ my: 1 }} />
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" fontWeight="bold">Marge</Typography>
                      <Typography variant="body2" fontWeight="bold" color="success.main">
                        {htg(rapport.articles.marge)}
                      </Typography>
                    </Box>
                  </>
                )}
              </Paper>
            )}

            {finances && !filtreClient && (
              <Paper sx={{ p: 2, flex: '1 1 340px' }}>
                <Typography variant="h6" gutterBottom>Dépenses par type</Typography>
                {rapport?.par_type_depense?.length ? rapport.par_type_depense.map((depense) => (
                  <Box key={depense.nom} display="flex" justifyContent="space-between" py={0.5}>
                    <Typography variant="body2">
                      {depense.nom} <Typography component="span" color="text.disabled">×{depense.nombre}</Typography>
                    </Typography>
                    <Typography variant="body2" fontWeight={600} color="error.main">
                      {htg(depense.total)}
                    </Typography>
                  </Box>
                )) : <Typography color="text.disabled">Aucune dépense</Typography>}
              </Paper>
            )}
          </Box>
        </>
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>Ticket</TableCell>
                  <TableCell>Payé le</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Paiement</TableCell>
                  <TableCell align="right">Montant</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rapport?.tickets?.length ? rapport.tickets.map((ticket) => (
                  <LigneTicket key={ticket.id} ticket={ticket} finances={finances} />
                )) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                      <ContentCut sx={{ fontSize: 48, color: 'text.disabled' }} />
                      <Typography color="text.secondary" mt={1}>
                        Aucun ticket sur cette période
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {rapport?.tickets_tronques && (
            <Alert severity="info" sx={{ m: 2 }}>
              Seuls les {rapport.tickets.length} tickets les plus récents sont affichés
              sur les {rapport.nombre_tickets} de la période — resserrez les dates pour tout voir.
            </Alert>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default StudioDashboard;
